import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  alpha,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Bolt as BoltIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useAuth } from "../../context/AuthContext";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: "blur(10px)",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[20],
  },
}));

const QuickActionCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
  color: theme.palette.primary.contrastText,
  borderRadius: theme.spacing(2),
  transition: "transform 0.3s ease-in-out",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const OverviewTab: React.FC = () => {
  const { user } = useAuth();

  const quickActions = [
    { title: "Submit Timesheet", icon: <BoltIcon /> },
    { title: "Request Leave", icon: <BoltIcon /> },
    { title: "View Paystubs", icon: <BoltIcon /> },
    { title: "Update Profile", icon: <BoltIcon /> },
  ];

  return (
    <Grid container spacing={3}>
      {/* Profile Card */}
      <Grid item xs={12} md={4}>
        <StyledCard>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <DashboardIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Your Profile</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Name:</strong> {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Email:</strong> {user?.email}
            </Typography>
            <Box mt={2}>
              <Chip
                icon={<CheckCircleIcon />}
                label={user?.status || "Active"}
                color="success"
                variant="outlined"
              />
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Next Steps Card */}
      <Grid item xs={12} md={4}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Next Steps
            </Typography>
            <Box>
              <Box display="flex" alignItems="center" mb={1}>
                <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="body2">Complete profile information</Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={1}>
                <PendingIcon color="warning" sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="body2">Review company policies</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <PendingIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="body2">Set up workspace</Typography>
              </Box>
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Resources Card */}
      <Grid item xs={12} md={4}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resources
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              • Employee Handbook
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              • Training Materials
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              • Support Contacts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • IT Setup Guide
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Quick Actions */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
          <BoltIcon color="primary" sx={{ mr: 1 }} />
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          {quickActions.map((action, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <QuickActionCard>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  {action.icon}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {action.title}
                  </Typography>
                </CardContent>
              </QuickActionCard>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default OverviewTab;